import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Container,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Slide
} from "@mui/material";
import {
  PersonAdd,
  Person,
  Work,
  Security,
  LocationOn,
  Description,
  Save,
  Clear,
  ArrowBack,
  CheckCircle
} from "@mui/icons-material";
import { postData } from "../../services/Request";
import { toast } from "react-toastify";
import Header from "../../components/AdminNavebar";
import { useNavigate } from "react-router-dom";

export default function UserProfileForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    gender: "",
    postcode: "",
    doi: "",
    certificate_no: "",
    countersignatory_details: "",
    registered_entity: "",
    countersignatory: "",
    dbs_children_barred_list: "",
    dbs_adults_barred_list: "",
    other_info_by_police: "",
    enhanced_certification: "",
    address: "",
    police_records: "",
    section_information: "",
    employer: "",
    position: "",
    employment_details: "",
  });

  const steps = [
    { label: "Personal Information", icon: <Person /> },
    { label: "DBS Details", icon: <Security /> },
    { label: "Employment Information", icon: <Work /> },
    { label: "Additional Information", icon: <Description /> }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setFormData({
      name: "",
      dob: "",
      gender: "",
      postcode: "",
      doi: "",
      certificate_no: "",
      countersignatory_details: "",
      registered_entity: "",
      countersignatory: "",
      dbs_children_barred_list: "",
      dbs_adults_barred_list: "",
      other_info_by_police: "",
      enhanced_certification: "",
      address: "",
      police_records: "",
      section_information: "",
      employer: "",
      position: "",
      employment_details: "",
    });
    setActiveStep(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await postData("api/profile/create", formData);
      if (res?.status === true) {
        toast.success("User profile created successfully!");
        handleReset();
        navigate("/admin/ProfileTable");
      } else {
        toast.error(res?.message || "Failed to create profile");
      }
    } catch (error) {
      toast.error("Error creating profile");
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Birth"
                name="dob"
                InputLabelProps={{ shrink: true }}
                value={formData.dob}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Issue"
                name="doi"
                InputLabelProps={{ shrink: true }}
                value={formData.doi}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Certificate Number"
                name="certificate_no"
                value={formData.certificate_no}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registered Entity"
                name="registered_entity"
                value={formData.registered_entity}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Countersignatory"
                name="countersignatory"
                value={formData.countersignatory}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Countersignatory Details"
                name="countersignatory_details"
                value={formData.countersignatory_details}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="DBS Children Barred List"
                name="dbs_children_barred_list"
                value={formData.dbs_children_barred_list}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter details or 'No' if not applicable"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="DBS Adults Barred List"
                name="dbs_adults_barred_list"
                value={formData.dbs_adults_barred_list}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter details or 'No' if not applicable"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Enhanced Certification"
                name="enhanced_certification"
                value={formData.enhanced_certification}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter details or 'No' if not applicable"
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employer"
                name="employer"
                value={formData.employer}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Employment Details"
                name="employment_details"
                value={formData.employment_details}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Other Information by Police"
                name="other_info_by_police"
                value={formData.other_info_by_police}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter any additional information provided by police"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Police Records"
                name="police_records"
                value={formData.police_records}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter details of any police records"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Section Information"
                name="section_information"
                value={formData.section_information}
                onChange={handleChange}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter any section-specific information"
              />
            </Grid>
          </Grid>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    }}>
      <Header />

      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        {/* Header Section */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: "white", 
                fontWeight: "bold", 
                mb: 1,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
              }}
            >
              Create User Profile
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ color: "rgba(255,255,255,0.8)" }}
            >
              Add a new DBS user profile to the system
            </Typography>
          </Box>
        </Fade>

        {/* Main Form Card */}
        <Slide direction="up" in timeout={1000}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)"
          }}>
            <CardContent sx={{ p: 4 }}>
              {/* Stepper */}
              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        StepIconComponent={({ active, completed }) => (
                          <Avatar
                            sx={{
                              bgcolor: active || completed ? "#667eea" : "rgba(0,0,0,0.1)",
                              color: active || completed ? "white" : "rgba(0,0,0,0.5)",
                              width: 40,
                              height: 40,
                              transition: "all 0.3s ease-in-out"
                            }}
                          >
                            {completed ? <CheckCircle /> : step.icon}
                          </Avatar>
                        )}
                      >
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Form Content */}
              <Box component="form" onSubmit={handleSubmit}>
                <Fade in timeout={600} key={activeStep}>
                  <Box>
                    {getStepContent(activeStep)}
                  </Box>
                </Fade>

                {/* Navigation Buttons */}
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  mt: 4,
                  pt: 3,
                  borderTop: "1px solid rgba(0,0,0,0.1)"
                }}>
                  <Box>
                    {activeStep > 0 && (
                      <Button
                        onClick={handleBack}
                        startIcon={<ArrowBack />}
                        sx={{
                          bgcolor: "rgba(0,0,0,0.1)",
                          color: "#333",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.2)"
                          }
                        }}
                      >
                        Back
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Tooltip title="Reset Form">
                      <IconButton
                        onClick={handleReset}
                        sx={{
                          bgcolor: "rgba(244, 67, 54, 0.1)",
                          color: "#f44336",
                          "&:hover": {
                            bgcolor: "rgba(244, 67, 54, 0.2)"
                          }
                        }}
                      >
                        <Clear />
                      </IconButton>
                    </Tooltip>

                    {activeStep < steps.length - 1 ? (
                      <Button
                        onClick={handleNext}
                        variant="contained"
                        sx={{
                          background: "linear-gradient(45deg, #667eea, #764ba2)",
                          "&:hover": {
                            background: "linear-gradient(45deg, #5a6fd8, #6a4190)"
                          }
                        }}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        sx={{
                          background: "linear-gradient(45deg, #4CAF50, #45a049)",
                          "&:hover": {
                            background: "linear-gradient(45deg, #45a049, #3d8b40)"
                          }
                        }}
                      >
                        {loading ? "Creating Profile..." : "Create Profile"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Slide>
      </Container>
    </Box>
  );
}
